"use client";

import { useState, useEffect, Suspense } from "react";
import { signIn, useSession } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { TestimonialsPanel } from "@/components/testimonials-panel";

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [utmParams, setUtmParams] = useState<string>("");
  const [hasSpacesInPassword, setHasSpacesInPassword] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: session } = useSession();

  // Capturar parâmetros UTM da URL
  useEffect(() => {
    const utmParamsArray: string[] = [];
    
    // Lista de parâmetros UTM que queremos capturar
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    
    utmKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        utmParamsArray.push(`${key}=${value}`);
      }
    });
    
    if (utmParamsArray.length > 0) {
      setUtmParams(utmParamsArray.join('&'));
    }
  }, [searchParams]);

  // Função para criar URL com UTMs preservados
  const createUrlWithUtms = (path: string) => {
    if (utmParams) {
      return `${path}?${utmParams}`;
    }
    return path;
  };

  useEffect(() => {
    if (password && password.includes(' ')) {
      setHasSpacesInPassword(true);
    } else {
      setHasSpacesInPassword(false);
      // Limpar erro de espaços quando não houver mais espaços
      if (error === "Por favor, remova os espaços da senha antes de continuar") {
        setError("");
      }
    }
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevenir submissão se houver espaços na senha
    if (password.includes(' ')) {
      setError("Por favor, remova os espaços da senha antes de continuar");
      return;
    }
    
    setIsLoading(true);
    setError("");

    try {
      const result = await signIn("credentials", {
        email: email.toLowerCase().trim(),
        password,
        redirect: false,
      });

      if (result?.error) {
        setError("Email ou senha inválidos");
      } else {
        router.push("/dashboard");
      }
    } catch {
      setError("Erro ao fazer login");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col lg:flex-row bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 overflow-hidden">
      {/* Left Panel - Login Section */}
      <div className="w-full lg:w-2/5 bg-white/80 backdrop-blur-sm flex items-center justify-center p-6 sm:p-8 lg:p-12 h-screen overflow-y-auto">
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="w-full mb-0 mt-[-50px] pb-0">
                  <Image
                    src="/logo-nova-vagaautomatica.png"
                    alt="Vaga Automática"
                    width={200}
                    height={100}
                    className="h-[160px] w-auto object-contain ml-[-20px] mb-[-40px]"
                    priority
                  />              
              </div>
            <h1 className="text-3xl font-bold text-gray-900">Bem-vindo de volta!</h1>
            <p className="text-gray-600">Acesse seu painel VagaAutomatica</p> 
          </div>
          {/* Login Form */}
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Login</h2>
              <p className="text-sm text-gray-600">
                Entre com suas credenciais para continuar
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-lg p-3">
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    E-mail
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase())}
                      className="pl-10 h-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Sua senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`pl-10 pr-10 h-10 ${hasSpacesInPassword ? 'border-amber-500 focus:border-amber-500 focus:ring-amber-500' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {hasSpacesInPassword && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-semibold mb-1">Atenção: Espaços detectados na senha</p>
                        <p className="text-xs leading-relaxed">
                          Sua senha contém espaços (no início, meio ou fim). Por favor, remova todos os espaços antes de fazer login. 
                          <span className="font-medium block mt-1">Dica: Cole a senha sem espaços extras.</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-1">
                  <div className="flex items-center space-x-2 gap-2">
                    <Checkbox
                      id="remember"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                    />
                    <Label htmlFor="remember" className="text-sm text-gray-700 cursor-pointer">
                      Lembrar-me
                    </Label>
                  </div>
                  <Link
                    href="/forgot-password"
                    className="text-sm text-primary hover:text-blue-700 hover:underline font-medium transition-colors"
                  >
                    Esqueceu a senha?
                  </Link>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="cursor-pointer w-full bg-primary from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-blue-500/30 transition-all duration-200 h-11"
                  disabled={isLoading || hasSpacesInPassword}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Entrando...
                    </>
                  ) : (
                    "Entrar no Painel"
                  )}
                </Button>

                <div className="text-center text-sm text-gray-600 pt-2">
                  Não tem uma conta?{" "}
                  <Link 
                    href={createUrlWithUtms("/register")} 
                    className="text-primary hover:text-blue-700 hover:underline font-semibold transition-colors"
                  >
                    Cadastre-se grátis
                  </Link>
                </div>
              </form>
          </div>
        </div>
      </div>

      {/* Right Panel - Promotional Section */}
      <TestimonialsPanel />

    </div>
  );
}

import { SessionProvider } from "next-auth/react";

export default function LoginPage() {
  return (
    <SessionProvider>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Carregando...</p>
          </div>
        </div>
      }>
        <LoginForm />
      </Suspense>
    </SessionProvider>
  );
}
